// FILE: src/components/search/SearchResultsList.jsx
import React, { useEffect, useRef } from "react";
import { sanitizeTitle } from "../../utils/searchHelpers";

/**
 * ARIA listbox with option roles and keyboard/mouse sync.
 * Uses item.displayTitle (preferred) or sanitized title.
 */

export default function SearchResultsList({ results = [], onSelect, activeIndex = -1, setActiveIndex = () => {} }) {
  const listRef = useRef(null);
  const itemRefs = useRef([]);

  itemRefs.current = results.map((_, i) => itemRefs.current[i] || React.createRef());

  useEffect(() => {
    if (activeIndex >= 0 && itemRefs.current[activeIndex] && itemRefs.current[activeIndex].current) {
      try {
        itemRefs.current[activeIndex].current.scrollIntoView({ block: "nearest", inline: "nearest" });
      } catch (e) {}
    }
  }, [activeIndex]);

  if (!results || results.length === 0) {
    return <div className="text-sm text-gray-500">No results. Try a different phrase.</div>;
  }

  // Flatten results (we keep order already sorted by search client)
  const flattened = results;

  return (
    <div className="relative">
      <div aria-live="polite" className="sr-only">
        {results.length} {results.length === 1 ? "result" : "results"} found
      </div>

      <div id="search-results-list" ref={listRef} role="listbox" aria-label="Search results" className="grid gap-3">
        {flattened.map((r, idx) => {
          const isActive = idx === activeIndex;
          const itemId = `search-result-${idx}`;
          // Prefer explicit displayTitle -> fallback to sanitized title -> fallback to raw title
          const display = r.displayTitle || sanitizeTitle(r.title || r.path || r.id) || r.title || (r.path ? r.path.split("/").pop() : r.id);

          // Small secondary subtitle (farm or path or description) — optional
          const subtitle = r.subtitle || r.description || (r.path && r.path.replace(/^\/+/, ""));

          return (
            <div
              key={r.id || `${idx}-${display}`}
              id={itemId}
              role="option"
              aria-selected={isActive}
              ref={itemRefs.current[idx]}
              onMouseEnter={() => setActiveIndex(idx)}
              className={`w-full text-left p-3 rounded-lg transition cursor-pointer ${
                isActive ? "bg-gray-100 dark:bg-neutral-800 ring-2 ring-green-300" : "hover:bg-gray-50 dark:hover:bg-neutral-800"
              }`}
              onClick={() => onSelect(r)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">{display}</div>
                  {subtitle && <div className="text-sm text-gray-500 dark:text-neutral-400">{subtitle}</div>}
                </div>
                <div className="ml-4 text-xs text-gray-400 whitespace-nowrap">{/* reserved small path if needed */}</div>
              </div>

              {typeof r.priority === "number" && r.priority > 0 && (
                <div className="mt-2 text-[11px] inline-block bg-gray-100 dark:bg-neutral-800 px-2 py-0.5 rounded">
                  Priority {r.priority}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
